import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { resourcesArticles as articles } from '@/lib/data/resources';
import { useTranslation } from 'react-i18next';
import { EmailText } from '@/components/ObfuscationSafeEmail';

const RESOURCES_DESCRIPTION =
  "Guides and articles on faithful giving, supporting missionaries, and stewardship. Resources for donors and Christian workers.";

export default function Resources() {
    usePageMeta('Resources', RESOURCES_DESCRIPTION);
    const { t } = useTranslation();

    const faqs = [
        { question: t('resourcesPage.faq.q1.question'), answer: t('resourcesPage.faq.q1.answer') },
        { question: t('resourcesPage.faq.q2.question'), answer: t('resourcesPage.faq.q2.answer') },
        { question: t('resourcesPage.faq.q3.question'), answer: t('resourcesPage.faq.q3.answer') },
        { question: t('resourcesPage.faq.q4.question'), answer: t('resourcesPage.faq.q4.answer') },
        { question: t('resourcesPage.faq.q5.question'), answer: t('resourcesPage.faq.q5.answer') },
    ];

    useEffect(() => {
        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        };
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-resources-faq-jsonld', 'true');
        el.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, [faqs]);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-extrabold tracking-tight text-gray-900">{t('resourcesPage.title')}</h1>
                    <p className="mt-4 text-lg text-gray-600">{t('resourcesPage.subtitle')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {articles.map((article) => (
                        <Link key={article.slug} to={`/resources/${article.slug}`} className="group block overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-xl transition-shadow">
                            <div className="aspect-[16/9] bg-gray-100 overflow-hidden">
                                <img src={article.image} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                            </div>
                            <div className="p-6">
                                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{t(`resourceArticles.${article.translationKey}.title`)}</h3>
                                <p className="mt-2 text-gray-600">{t(`resourceArticles.${article.translationKey}.excerpt`)}</p>
                                <div className="mt-4 inline-flex items-center text-blue-600 group-hover:text-blue-700 font-medium">
                                    {t('resourcesPage.readArticle')}
                                    <svg className="ml-2 h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 01-.083 1.32l-5 5a1 1 0 11-1.414-1.414L13.586 10H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* FAQ */}
                <div className="max-w-3xl mx-auto mt-20">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('resourcesPage.faq.title')}</h2>
                        <p className="text-lg text-gray-600">{t('resourcesPage.faq.subtitle')}</p>
                    </div>
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                                <p className="text-gray-600 leading-relaxed"><EmailText text={faq.answer} /></p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}


